import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Trash2, Pencil, Check, Bell, BellOff, Calendar } from 'lucide-react';
import { useSavedSearchStore, useCarStore } from '@/store';
import { Button } from '@/components/ui';
import type { SavedSearch, CarFilters } from '@/types';

interface SavedSearchesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FilterTagProps {
  children: React.ReactNode;
}

function FilterTag({ children }: FilterTagProps) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 bg-gray-100 text-gray-700 text-xs rounded-full whitespace-nowrap">
      {children}
    </span>
  );
}

function getFilterTags(filters: CarFilters, t: (key: string) => string): string[] {
  const tags: string[] = [];

  if (filters.make) tags.push(filters.make);
  if (filters.model) tags.push(filters.model);
  if (filters.yearMin || filters.yearMax) {
    if (filters.yearMin && filters.yearMax) {
      tags.push(`${filters.yearMin}-${filters.yearMax}`);
    } else if (filters.yearMin) {
      tags.push(`${filters.yearMin}+`);
    } else {
      tags.push(`-${filters.yearMax}`);
    }
  }
  if (filters.priceMin || filters.priceMax) {
    if (filters.priceMin && filters.priceMax) {
      tags.push(`$${filters.priceMin.toLocaleString()}-$${filters.priceMax.toLocaleString()}`);
    } else if (filters.priceMin) {
      tags.push(`$${filters.priceMin.toLocaleString()}+`);
    } else {
      tags.push(`-$${filters.priceMax?.toLocaleString()}`);
    }
  }
  if (filters.mileageMin || filters.mileageMax) {
    if (filters.mileageMin && filters.mileageMax) {
      tags.push(`${filters.mileageMin.toLocaleString()}-${filters.mileageMax.toLocaleString()} km`);
    } else if (filters.mileageMin) {
      tags.push(`${filters.mileageMin.toLocaleString()}+ km`);
    } else {
      tags.push(`-${filters.mileageMax?.toLocaleString()} km`);
    }
  }
  if (filters.city) tags.push(filters.city);
  if (filters.region) tags.push(filters.region);
  if (filters.fuelType) tags.push(t(`fuelTypes.${filters.fuelType}`));
  if (filters.transmission) tags.push(t(`transmissions.${filters.transmission}`));
  if (filters.bodyType) tags.push(t(`bodyTypes.${filters.bodyType}`));
  if (filters.colors?.length) {
    tags.push(filters.colors.map((color) => t(`colors.${color}`)).join(', '));
  } else if (filters.color) {
    tags.push(t(`colors.${filters.color}`));
  }
  if (filters.sellerType) tags.push(t(`sellerTypes.${filters.sellerType}`));
  if (filters.hasWarranty) tags.push(t('advancedFilters.withWarranty'));
  if (filters.features && filters.features.length > 0) {
    tags.push(`${filters.features.length} ${t('carDetail.features')}`);
  }

  return tags;
}

function formatDate(dateString: string, locale: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(locale === 'ar' ? 'ar-IQ' : locale === 'ku' ? 'ckb-IQ' : 'en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function SavedSearchItem({
  search,
  onApply,
  onDelete,
  onUpdate,
  onToggleNotifications,
}: {
  search: SavedSearch;
  onApply: (search: SavedSearch) => void;
  onDelete: (id: string) => void;
  onUpdate: (id: string, name: string) => void;
  onToggleNotifications: (id: string, enabled: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(search.name);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const filterTags = getFilterTags(search.filters, t);

  const handleSave = () => {
    if (editName.trim()) {
      onUpdate(search.id, editName.trim());
      setIsEditing(false);
    }
  };

  const handleDelete = () => {
    onDelete(search.id);
    setShowDeleteConfirm(false);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
    >
      {/* Header: Name + Date + Actions */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSave();
                  if (e.key === 'Escape') {
                    setEditName(search.name);
                    setIsEditing(false);
                  }
                }}
              />
              <button
                onClick={handleSave}
                className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setEditName(search.name);
                  setIsEditing(false);
                }}
                className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <h3 className="font-bold text-lg text-gray-900 truncate">{search.name}</h3>
              <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500">
                <Calendar className="w-3.5 h-3.5" />
                <span>{t('savedSearches.lastUpdated', { date: formatDate(search.updatedAt, i18n.language) })}</span>
              </div>
            </>
          )}
        </div>

        {!isEditing && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title={t('common.edit')}
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title={t('common.delete')}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Filter Tags */}
      {filterTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {filterTags.map((tag, index) => (
            <FilterTag key={index}>{tag}</FilterTag>
          ))}
        </div>
      )}
      {filterTags.length === 0 && (
        <div className="mt-3">
          <FilterTag>{t('savedSearches.allCars')}</FilterTag>
        </div>
      )}

      {/* Footer: Email toggle + CTA */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
        {/* Email Notification Toggle */}
        <button
          onClick={() => onToggleNotifications(search.id, !search.emailNotifications)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
            search.emailNotifications
              ? 'bg-primary-50 text-primary-700 hover:bg-primary-100'
              : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
          }`}
          title={t('savedSearches.emailNotificationsHint')}
        >
          {search.emailNotifications ? (
            <Bell className="w-4 h-4" />
          ) : (
            <BellOff className="w-4 h-4" />
          )}
          <span className="hidden sm:inline">
            {search.emailNotifications
              ? t('savedSearches.emailNotificationsOn')
              : t('savedSearches.emailNotificationsOff')}
          </span>
        </button>

        {/* CTA Button */}
        <Button
          onClick={() => onApply(search)}
          className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-4 py-2"
        >
          <Search className="w-4 h-4 mr-2" />
          {t('savedSearches.viewResults', { count: search.resultCount || 0 })}
        </Button>
      </div>

      {/* Delete confirmation */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-3 pt-3 border-t border-gray-100"
          >
            <p className="text-sm text-gray-600 mb-2">{t('savedSearches.confirmDelete')}</p>
            <div className="flex gap-2">
              <Button size="sm" variant="danger" onClick={handleDelete}>
                {t('common.delete')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function SavedSearchesModal({ isOpen, onClose }: SavedSearchesModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    savedSearches,
    fetchSavedSearches,
    updateSavedSearchName,
    removeSavedSearch,
    toggleEmailNotifications,
    isLoading,
  } = useSavedSearchStore();
  const { setFilters, setSorting } = useCarStore();

  useEffect(() => {
    if (isOpen) {
      fetchSavedSearches();
    }
  }, [isOpen, fetchSavedSearches]);

  const handleApply = (search: SavedSearch) => {
    // Apply filters to the store
    setFilters(search.filters);
    setSorting(
      search.sortBy as 'createdAt' | 'price' | 'mileageKm' | 'year',
      search.sortOrder as 'asc' | 'desc'
    );
    onClose();
    navigate('/search');
  };

  const handleDelete = async (id: string) => {
    await removeSavedSearch(id);
  };

  const handleUpdate = async (id: string, name: string) => {
    await updateSavedSearchName(id, name);
  };

  const handleToggleNotifications = async (id: string, enabled: boolean) => {
    await toggleEmailNotifications(id, enabled);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-4 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-w-xl sm:w-full bg-white rounded-2xl shadow-2xl z-50 flex flex-col max-h-[90vh]"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <Search className="w-5 h-5 text-primary-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('savedSearches.title')}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : savedSearches.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <Search className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500">{t('savedSearches.noSavedSearches')}</p>
                  <p className="text-sm text-gray-400 mt-1">{t('savedSearches.noSavedSearchesHint')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <AnimatePresence mode="popLayout">
                    {savedSearches.map((search) => (
                      <SavedSearchItem
                        key={search.id}
                        search={search}
                        onApply={handleApply}
                        onDelete={handleDelete}
                        onUpdate={handleUpdate}
                        onToggleNotifications={handleToggleNotifications}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
